import { Box } from "@mantine/core";
import { TFunction } from "i18next";
import { useState } from "react";
import { Control, FormProvider, useForm, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useCompany } from "#modules/company/view/useCompany";
import { useQuoteRequestWeight } from "#modules/quoteRequests/view/useQuoteRequestWeight";
import { useUpdateQuoteRequestTransport } from "#modules/quoteRequests/view/useUpdateQuoteRequestTransport";
import { CheckboxController } from "#shared/view/components/CheckboxController";
import { Flex } from "#shared/view/components/Flex";
import { Group } from "#shared/view/components/Group";
import { Modal } from "#shared/view/components/Modal";
import { PriceInputController } from "#shared/view/components/PriceInputController";
import { Stack } from "#shared/view/components/Stack";
import { Text } from "#shared/view/components/Text";
import { Button } from "#shared/view/designSystem/Button";
import { SelectInputController } from "#shared/view/designSystem/inputs/SelectInputController";
import { Skeleton } from "#shared/view/designSystem/Skeleton";
import { TextButton } from "#shared/view/designSystem/TextButton";
import { Tooltip } from "#shared/view/designSystem/Tooltip";
import {
  formatPrice,
  round,
  TransportCalculationMethodSchema,
  TransportCalculationMethodType,
} from "shared";

type TransportSelectForm = {
  method: TransportCalculationMethodType;
  transportCost: number;
  shouldDistributeCost: boolean;
  distributionMethod: "weight";
  isOutsourced: boolean;
};

const TRANSPORT_SELECT_METHOD_OPTIONS: (t: TFunction) => {
  value: TransportSelectForm["method"];
  label: string;
}[] = (t) => [
  {
    value: TransportCalculationMethodSchema.enum.FIXED,
    label: t("quoteRequest.transport.fixedCost"),
  },
  {
    value: TransportCalculationMethodSchema.enum.WEIGHT,
    label: t("quoteRequest.transport.distributeByWeight"),
  },
];

const TRANSPORT_SELECT_DISTRIBUTION_METHOD_OPTIONS: (t: TFunction) => {
  value: TransportSelectForm["distributionMethod"];
  label: string;
}[] = (t) => [{ value: "weight", label: t("quoteRequest.transport.distributeByWeight") }];

export const TransportSelectModal = ({
  quoteRequestId,
  isOpen,
  close,
  transportTotalCost,
  hasTransport,
  hasBatches,
}: {
  quoteRequestId: string;
  isOpen: boolean;
  close: () => void;
  transportTotalCost: number;
  hasTransport: boolean;
  hasBatches: boolean;
}) => {
  const [isEdit, setIsEdit] = useState(!hasTransport);

  const { t } = useTranslation();
  const { company } = useCompany();
  const { quoteRequestWeight, isFetching } = useQuoteRequestWeight(quoteRequestId, isOpen);

  const methods = useForm<TransportSelectForm>({
    defaultValues: {
      method:
        company?.companySettings.defaultTransportCalculationMethod ??
        TransportCalculationMethodSchema.enum.FIXED,
      transportCost: 0,
      shouldDistributeCost: company?.companySettings.defaultTransportShouldDistributeCost ?? false,
      distributionMethod: "weight",
      isOutsourced: company?.companySettings.defaultTransportIsOutsourced ?? true,
    },
  });

  const { reset, control } = methods;

  const transportCost = useWatch({ control, name: "transportCost" });
  const shouldDistributeCost = useWatch({ control, name: "shouldDistributeCost" });
  const distributionMethod = useWatch({ control, name: "distributionMethod" });
  const method = useWatch({ control, name: "method" });
  const isOutsourced = useWatch({ control, name: "isOutsourced" });

  const resetAndClose = () => {
    setIsEdit(false);
    reset();
    close();
  };

  const { updateQuoteRequestTransport } = useUpdateQuoteRequestTransport({
    onSuccess: resetAndClose,
  });

  const upsertTransportProcessStep = async () => {
    updateQuoteRequestTransport({
      id: quoteRequestId,
      payload: {
        transportCost,
        method,
        shouldDistributeCost,
        distributionMethod,
        isOutsourced,
      },
    });
  };

  return (
    <Modal
      opened={isOpen}
      onClose={resetAndClose}
      withCloseButton={false}
      size="lg"
      title={t("quoteRequest.transport.modalTitle")}
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(upsertTransportProcessStep)}>
          <Stack gap="xs">
            {isEdit ? (
              <TransportSelectModalEdit hasBatches={hasBatches} />
            ) : (
              <TransportSelectModalView
                transportTotalCost={transportTotalCost}
                modifyTransport={() => setIsEdit(true)}
              />
            )}

            <Flex align="center" gap={4}>
              <Text size="sm">{t("quoteRequest.transport.weight")} : </Text>
              {isFetching ? (
                <Skeleton width={40} height={16} />
              ) : (
                <Text fw="bold" size="sm">{`${round(quoteRequestWeight, 2)}kg`}</Text>
              )}
            </Flex>
            <Group justify="end" gap="xs">
              <Button variant="outline" onClick={resetAndClose}>
                {isEdit ? t("common.cancel") : t("common.close")}
              </Button>
              {isEdit && <Button type="submit">{t("common.validate")}</Button>}
            </Group>
          </Stack>
        </form>
      </FormProvider>
    </Modal>
  );
};

const TransportSelectModalView = ({
  transportTotalCost,
  modifyTransport,
}: {
  transportTotalCost: number;
  modifyTransport: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <Flex align="center" gap="xs" pt={4}>
      <Flex align="center" gap={4}>
        <Text size="sm">{t("quoteRequest.transport.total")} : </Text>
        <Text fw="bold" size="sm">
          {formatPrice(transportTotalCost)}
        </Text>
      </Flex>
      <TextButton onClick={modifyTransport}>{t("quoteRequest.transport.modify")}</TextButton>
    </Flex>
  );
};

const TransportSelectModalEdit = ({ hasBatches }: { hasBatches: boolean }) => {
  const { t } = useTranslation();

  const { control, setValue } = useFormContext<TransportSelectForm>();

  const shouldDistributeCost = useWatch({ control, name: "shouldDistributeCost" });
  const selectedMethod = useWatch({ control, name: "method" });

  const canDistributeCost = ({ method }: { method: TransportCalculationMethodType }) => {
    return !hasBatches || method === TransportCalculationMethodSchema.enum.WEIGHT;
  };

  return (
    <Stack gap="xs" mb="sm">
      <Group gap="xs">
        <SelectInputController
          name="method"
          data={TRANSPORT_SELECT_METHOD_OPTIONS(t)}
          control={control}
          label={t("quoteRequest.transport.method")}
          withEmptyData={false}
          allowDeselect={false}
          onChange={(value: TransportCalculationMethodType) => {
            if (shouldDistributeCost && !canDistributeCost({ method: value })) {
              setValue("shouldDistributeCost", false);
            }
          }}
        />
        <PriceInputController
          label={
            selectedMethod === TransportCalculationMethodSchema.enum.WEIGHT
              ? t("quoteRequest.transport.pricePerKg")
              : t("quoteRequest.transport.price")
          }
          control={control}
          name="transportCost"
          unit={
            selectedMethod === TransportCalculationMethodSchema.enum.WEIGHT ? "€/kg" : undefined
          }
        />
      </Group>

      <CheckboxController
        name="isOutsourced"
        control={control}
        label={t("quoteRequest.transport.outsourced")}
      />

      {canDistributeCost({ method: selectedMethod }) ? (
        <DistributeCostCheckboxController control={control} />
      ) : (
        <Tooltip label={t("quoteRequest.transport.distributeCostTooltip")}>
          <Box style={{ cursor: "not-allowed", display: "inline-block" }}>
            <DistributeCostCheckboxController control={control} disabled />
          </Box>
        </Tooltip>
      )}

      {shouldDistributeCost && selectedMethod === TransportCalculationMethodSchema.enum.FIXED && (
        <Group gap="xs">
          <SelectInputController
            name="distributionMethod"
            control={control}
            data={TRANSPORT_SELECT_DISTRIBUTION_METHOD_OPTIONS(t)}
            withEmptyData={false}
            allowDeselect={false}
            label={t("quoteRequest.transport.distributionMethod")}
            disabled
          />
        </Group>
      )}
    </Stack>
  );
};

const DistributeCostCheckboxController = ({
  control,
  disabled,
}: {
  control: Control<TransportSelectForm>;
  disabled?: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <CheckboxController
      name="shouldDistributeCost"
      control={control}
      label={t("quoteRequest.transport.distributeCost")}
      disabled={disabled}
    />
  );
};
